/**
 * Chat V2 — module-wide pure helpers. No React, no side effects. Feature-
 * specific helpers live next to their feature (e.g. `lib/markdown/utils.ts`,
 * `components/PdfViewer/utils.ts`); this file is for logic shared across the
 * module.
 */

import type { OpenedDocument } from 'src/content/ChatV2/context/ChatUIContext';
import { chatApi } from 'src/newServices/chatApi';
import type { ChatData } from 'src/types/chat';

import type { NewFile, SendMessagePayload } from './commands';
import { seedComposerSettings } from './components/Composer/composerSettings';
import type { FileTab } from './components/PdfViewer/types';
import { CLIENT_SESSION_KEY_PREFIX, NEW_CHAT_ROUTE } from './constants';
import type { CHAT_TOOL, KNOWLEDGE_TYPE } from './enums';
import { MESSAGE_TYPE } from './enums';
import type { RawChatMessage } from './lib/messageMappers';
import {
  fallbackTurnSettings,
  TURN_SETTINGS_FIELD,
  turnSettingsOfPayload,
  turnSettingsOfRow
} from './lib/turnSettings';
import type { ChatId } from './types/ids';

/** True when the URL points at a brand-new chat rather than an existing one. */
export function isNewChatRoute(routeChatId?: string): boolean {
  return !routeChatId || routeChatId === NEW_CHAT_ROUTE;
}

/** Stable client session key for a brand-new chat (the registry key — never
 * sent to the BE). Only needs to be unique per mount; `randomUUID` when
 * available. */
export function freshSessionKey(): ChatId {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return `${CLIENT_SESSION_KEY_PREFIX}${crypto.randomUUID()}`;
  }
  return `${CLIENT_SESSION_KEY_PREFIX}${Date.now()}`;
}

/** Whether `chatId` is a client session key rather than a server chat id. */
export function isClientSessionKey(chatId: string | number): boolean {
  return String(chatId).startsWith(CLIENT_SESSION_KEY_PREFIX);
}

/**
 * Does this chat exist on the server — a server chat id, or a client session key
 * whose first POST already returned `serverChatId`? Decides "load it (skeleton)"
 * vs "brand-new chat (welcome)".
 */
export function isSavedChat(
  chatId: string | number,
  serverChatId?: string | number
): boolean {
  return !isClientSessionKey(chatId) || serverChatId != null;
}

/**
 * Stable client-generated identity for a single message row (query or response),
 * generated at submit BEFORE any network call.
 */
export function createMessageClientId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `m-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Build the RTK Query thunk that appends a BE-shaped message row to an existing chat's
 * cached `getMessages` page. Pure — it returns the thunk; the caller `dispatch`es it.
 */
export function appendMessageToCache(chatId: ChatId, row: RawChatMessage) {
  return chatApi.util.updateQueryData(
    'getMessages',
    { params: { chat_id: chatId } },
    (draft: { data?: { msgs?: RawChatMessage[] } }) => {
      if (!draft?.data?.msgs) {
        return;
      }
      draft.data.msgs.push(row);
    }
  );
}

/**
 * Build the RTK Query thunk that optimistically patches a message's `reaction_code` in
 * the cached `getMessages` page.
 */
export function updateMessageReaction(
  chatId: ChatId,
  messageId: string,
  reactionCode: string | null
) {
  return chatApi.util.updateQueryData(
    'getMessages',
    { params: { chat_id: chatId } },
    (draft: { data?: { msgs?: RawChatMessage[] } }) => {
      const msgs = draft?.data?.msgs;
      if (!msgs) {
        return;
      }
      const msg = msgs.find((m) => String(m.id) === messageId);
      if (msg) {
        msg.reaction_code = reactionCode;
      }
    }
  );
}

/**
 * Build the RTK Query thunk that prepares a cached message list for a REGENERATE
 * or EDIT of the turn rooted at `queryId`: drop every row after the query, and —
 * for an edit — overwrite the query's own `message` with `editedText`.
 */
export function truncateAfterQuery(
  chatId: ChatId,
  queryId: string,
  editedText?: string
) {
  return chatApi.util.updateQueryData(
    'getMessages',
    { params: { chat_id: chatId } },
    (draft: { data?: { msgs?: RawChatMessage[] } }) => {
      const msgs = draft?.data?.msgs;
      if (!msgs) {
        return;
      }
      const idx = msgs.findIndex((m) => String(m.id) === queryId);
      if (idx === -1) {
        return;
      }
      msgs.splice(idx + 1);
      if (editedText !== undefined) {
        msgs[idx] = { ...msgs[idx], message: editedText };
      }
    }
  );
}

/**
 * Build the optimistic user-query row appended to the cache the instant a turn is
 * submitted — before any network call. `clientId === id` so the row keys stably and
 * survives the later `serverId` patch.
 */
export function buildOptimisticQueryRow(
  queryClientId: string,
  payload: SendMessagePayload
): RawChatMessage {
  return {
    clientId: queryClientId,
    id: queryClientId,
    type: MESSAGE_TYPE.QUERY,
    message: payload.text,
    status: 'pending',
    c_ts: new Date().toISOString(),
    ...(payload.newFiles &&
      payload.newFiles.length > 0 && { resources: payload.newFiles }),
    skills: payload.skills ?? [],
    [TURN_SETTINGS_FIELD]: turnSettingsOfPayload(payload)
  };
}

/**
 * Map the chat's opened documents to PdfViewer tabs. Only PDFs are opened in
 * the viewer (non-PDF sources download instead — see `useOpenSource`), so the
 * tab `type` is always `'pdf'`.
 */
export function openedDocumentsToTabs(docs: OpenedDocument[]): FileTab[] {
  return docs.map((doc) => ({
    id: doc.id,
    name: doc.name,
    url: doc.url,
    type: 'pdf',
    currentPage: doc.initialPage
  }));
}

/**
 * Everything a RE-RUN of an existing turn must carry: the turn's settings plus the
 * attachments the question was asked with.
 */
export function buildRerunPayload({
  queryRow,
  chatData,
  chatType,
  allowlist
}: {
  queryRow: RawChatMessage;
  chatData?: ChatData;
  chatType: KNOWLEDGE_TYPE;
  allowlist?: readonly CHAT_TOOL[];
}): Partial<SendMessagePayload> {
  const stamped = turnSettingsOfRow(queryRow);
  const settings =
    stamped ??
    (() => {
      const seed = seedComposerSettings(chatData, undefined);
      return fallbackTurnSettings({
        chatType,
        activeTools: seed.activeTools,
        imageGenTool: seed.imageGenTool,
        allowlist
      });
    })();

  const resources = queryRow.resources;
  const newFiles =
    Array.isArray(resources) && resources.length > 0
      ? (resources as NewFile[])
      : undefined;

  return { ...settings, ...(newFiles && { newFiles }) };
}
